import React from 'react';
import { Footer, GradientHero, SEO } from '@coupling/ui';
import Header from '../components/Header';
import { seoConfig } from '../config/seo';

const Privacy: React.FC = () => {
  return (
    <div className="bg-white min-h-screen">
      <SEO
        title={`Privacy Policy - ${seoConfig.siteName}`}
        description="How Coupling WNC collects, uses, and protects your information."
        canonical={`${seoConfig.siteUrl}/privacy`}
        ogImage={seoConfig.defaultOgImage}
        ogUrl={`${seoConfig.siteUrl}/privacy`}
      />
      <Header />

      <GradientHero
        title="Privacy Policy"
        description="How we collect, use, and protect your information."
      />

      {/* Privacy Policy Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-8 sm:p-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-neutral-900 mb-2">
            Privacy Policy
          </h1>
          <p className="text-neutral-500 mb-8">
            Last Updated: January 5, 2026
          </p>

          <div className="prose prose-neutral max-w-none text-neutral-600">
            <p className="text-lg leading-relaxed mb-8">
              This Privacy Policy describes how <strong>Coupling</strong> ("we," "us," or "our") collects, uses, stores, and protects personal information of merchants ("Tenants"), their customers ("End Users"), and POS operators ("Staff") who use the Coupling platform, white-labeled mobile applications, or related services.
            </p>

            <hr className="border-neutral-200 my-8" />

            <h2 className="text-2xl font-bold text-neutral-900 mt-8 mb-4">1. Information We Collect</h2>
            <p>We collect the following categories of information:</p>

            <h3 className="text-xl font-semibold text-neutral-900 mt-6 mb-3">1.1 Account & Contact Information</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Tenant business name, email, phone</li>
              <li>Staff login information</li>
              <li>User account identifiers</li>
              <li>Device information (device ID, model, OS version)</li>
            </ul>

            <h3 className="text-xl font-semibold text-neutral-900 mt-6 mb-3">1.2 Transactional Information</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Token issuance, redemption, and transfer metadata</li>
              <li>Merchant-to-merchant partnership selections</li>
              <li>POS usage events (time, amount, merchant ID)</li>
            </ul>

            <h3 className="text-xl font-semibold text-neutral-900 mt-6 mb-3">1.3 App Usage Information</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Log events and analytics</li>
              <li>Crash logs</li>
              <li>Feature engagement metrics</li>
            </ul>

            <h3 className="text-xl font-semibold text-neutral-900 mt-6 mb-3">1.4 Optional Information</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Uploaded profile images/logos</li>
              <li>Customer names or nicknames <em>if provided by Tenant</em></li>
              <li>Storefront information</li>
            </ul>

            <p className="mt-4">
              Coupling <strong>does not</strong> collect payment card numbers, banking credentials, Social Security numbers, or other sensitive financial data.
            </p>

            <hr className="border-neutral-200 my-8" />

            <h2 className="text-2xl font-bold text-neutral-900 mt-8 mb-4">2. How We Use Information</h2>
            <p>We use collected information to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide the platform and mobile apps</li>
              <li>Manage authentication and permissions</li>
              <li>Enable token issuance, transfer, and redemption</li>
              <li>Process analytics for Tenants</li>
              <li>Improve system performance</li>
              <li>Detect abuse or misuse</li>
              <li>Provide customer support</li>
            </ul>
            <p className="mt-4">We <strong>do not sell</strong> your data.</p>

            <hr className="border-neutral-200 my-8" />

            <h2 className="text-2xl font-bold text-neutral-900 mt-8 mb-4">3. How We Share Information</h2>
            <p>We may share information:</p>

            <h3 className="text-xl font-semibold text-neutral-900 mt-6 mb-3">3.1 With Tenants</h3>
            <p>End User activity is visible <strong>only to the Tenant whose tokens the user interacts with</strong>.</p>

            <h3 className="text-xl font-semibold text-neutral-900 mt-6 mb-3">3.2 With Third-Party Service Providers</h3>
            <p>For:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Hosting</li>
              <li>Analytics</li>
              <li>Push notifications</li>
              <li>Security</li>
            </ul>
            <p className="mt-2">These providers are contractually required to safeguard data.</p>

            <h3 className="text-xl font-semibold text-neutral-900 mt-6 mb-3">3.3 When Required by Law</h3>
            <p>Only if legally mandated (court order, warrant, etc.).</p>

            <hr className="border-neutral-200 my-8" />

            <h2 className="text-2xl font-bold text-neutral-900 mt-8 mb-4">4. Data Security</h2>
            <p>Coupling employs:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Encryption in transit</li>
              <li>Regular security reviews</li>
              <li>Access control</li>
              <li>Least-privilege internal access</li>
            </ul>
            <p className="mt-4">No system is 100% secure; users must secure their own devices.</p>

            <hr className="border-neutral-200 my-8" />

            <h2 className="text-2xl font-bold text-neutral-900 mt-8 mb-4">5. Data Retention</h2>
            <p>We retain data as long as:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>A Tenant account remains active</li>
              <li>Required for compliance</li>
              <li>Needed to maintain reliable analytics</li>
            </ul>
            <p className="mt-4">Data may be anonymized for long-term system improvement.</p>

            <hr className="border-neutral-200 my-8" />

            <h2 className="text-2xl font-bold text-neutral-900 mt-8 mb-4">6. User Rights</h2>
            <p>Users may:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Request deletion of personal data</li>
              <li>Request correction of inaccurate data</li>
              <li>Request export of app data</li>
            </ul>
            <p className="mt-4">Requests may be submitted through the Tenant or directly to Coupling.</p>

            <hr className="border-neutral-200 my-8" />

            <h2 className="text-2xl font-bold text-neutral-900 mt-8 mb-4">7. International Transfers</h2>
            <p>Coupling may transfer data internationally using:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Standard Contractual Clauses (SCC)</li>
              <li>Adequacy decisions</li>
              <li>Other lawful mechanisms</li>
            </ul>

            <hr className="border-neutral-200 my-8" />

            <h2 className="text-2xl font-bold text-neutral-900 mt-8 mb-4">8. Children's Privacy</h2>
            <p>
              Coupling does <strong>not</strong> target children under 13. Tenants must not knowingly onboard minors without consent where required.
            </p>

            <hr className="border-neutral-200 my-8" />

            <h2 className="text-2xl font-bold text-neutral-900 mt-8 mb-4">9. Changes</h2>
            <p>We may update this Privacy Policy from time to time. Continued use constitutes acceptance.</p>

            <hr className="border-neutral-200 my-8" />

            <h2 className="text-2xl font-bold text-neutral-900 mt-8 mb-4">10. Contact</h2>
            <div className="bg-brand-primary/5 rounded-xl p-6 border border-brand-primary/20">
              <p className="font-semibold text-neutral-900 mb-2">Coupling</p>
              <p>
                Email: <a href="mailto:hello@cplng.org" className="text-brand-primary hover:text-brand-primary-dark underline">hello@cplng.org</a>
              </p>
              <p className="mt-2">
                Address: 1793 Glen Cannon Dr, Pisgah Forest, NC 28768
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Privacy;
